// Admin console: Database — a read-only view of the application's own
// Postgres tables, with filtering and an Excel/CSV export.
//
// Regions follow the Akun screen's conventions (ui.region_gate):
//   header    plain spacing, hairline below   h1, table count
//   toolbar   divided section                 table picker, search, column picker, export
//   table     rows                            table from 1024, stacked rows below
//
// Read-only by construction: there is no write path in the API module behind
// this screen, so the UI deliberately offers no edit affordance at all rather
// than disabled buttons that imply one exists.
import { useEffect, useId, useMemo, useState } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import {
  EmptyState,
  Notice,
  PageHeader,
  SearchInput,
  SkeletonRows,
  useAsync,
  useTitle,
} from "../ui/components";
import { Glyph } from "../ui/icons";

const PAGE_SIZE = 50;

/** Cell rendering: nulls read as an explicit placeholder, not an empty gap. */
export function renderCell(value: unknown): { text: string; muted: boolean } {
  if (value === null || value === undefined) return { text: "kosong", muted: true };
  if (typeof value === "boolean") return { text: value ? "true" : "false", muted: false };
  if (typeof value === "object") {
    try {
      return { text: JSON.stringify(value), muted: false };
    } catch {
      return { text: String(value), muted: false };
    }
  }
  const text = String(value);
  if (text === "") return { text: "kosong", muted: true };
  return { text, muted: false };
}

/** Long values would stretch a column past the viewport; the title shows the rest. */
export function truncate(text: string, max = 80): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * The display name for a foreign-key cell, or null when there is none.
 *
 * Returns null (rather than a placeholder) for an id whose target row is gone,
 * so the raw value is shown as-is instead of claiming a name we do not have.
 */
export function labelFor(
  col: API.DbColumn,
  value: unknown,
  labels: Record<string, Record<string, API.DbRowLabel>>,
): API.DbRowLabel | null {
  if (!col.references) return null;
  if (value === null || value === undefined || value === "") return null;
  const found = labels[col.name]?.[String(value)];
  if (!found) return null;
  // A row with neither a name nor an owner carries no information.
  if (!found.name && !found.extra) return null;
  return found;
}

/**
 * What a reference cell reads as: the target's name, and the owner one hop
 * further when there is one. An untitled target falls back to the owner alone
 * rather than rendering an empty line.
 */
export function refLines(label: API.DbRowLabel): { primary: string; secondary?: string } {
  if (!label.name) return { primary: label.extra ?? "" };
  return label.extra ? { primary: label.name, secondary: label.extra } : { primary: label.name };
}

export function DatabaseScreen() {
  useTitle("Database");
  const ids = useId();

  const tables = useAsync(() => API.listDbTables(), []);
  const [table, setTable] = useState<string>("");
  const [q, setQ] = useState("");
  const [column, setColumn] = useState("");
  const [page, setPage] = useState(1);
  const [exportError, setExportError] = useState<string | null>(null);

  // Pick the first table once the list arrives, so the screen is never an
  // empty shell waiting for a click.
  const tableList = tables.data ?? [];
  useEffect(() => {
    if (!table && tableList.length > 0) setTable(tableList[0]!.name);
  }, [table, tableList]);

  // A new table or filter invalidates the current offset: staying on page 7
  // of a table that now has one page shows a confusing empty result.
  useEffect(() => {
    setPage(1);
  }, [table, q, column]);

  const data = useAsync(
    () =>
      table
        ? API.browseDbTable(table, {
            q: q.trim() || undefined,
            column: column || undefined,
            page,
            pageSize: PAGE_SIZE,
          })
        : Promise.resolve(null),
    [table, q, column, page],
  );

  const page_ = data.data;
  const columns = page_?.columns ?? [];
  const rows = page_?.rows ?? [];
  const labels = page_?.labels ?? {};
  const total = page_?.total ?? 0;
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const filtered = Boolean(q.trim());

  const filterable = useMemo(() => columns.filter((col) => col.filterable), [columns]);

  function exportAs(format: "xlsx" | "csv") {
    if (!table) return;
    setExportError(null);
    try {
      const url = API.dbExportUrl(table, { q: q.trim() || undefined, column: column || undefined }, format);
      // A plain navigation lets the browser's own download UI handle it and
      // keeps the session cookie attached.
      window.location.assign(url);
    } catch {
      setExportError("Unduhan gagal dimulai. Coba lagi.");
    }
  }

  return (
    <>
      <PageHeader
        divided
        title="Database"
        lead={
          tables.data
            ? `${formatCount(tableList.length)} tabel · hanya baca, data tidak dapat diubah dari halaman ini`
            : "Hanya baca — data tidak dapat diubah dari halaman ini."
        }
      />

      <section className="toolbar" aria-label="Saring data">
        <div className="field">
          <label htmlFor={`${ids}-table`}>Tabel</label>
          <select
            id={`${ids}-table`}
            value={table}
            onChange={(e) => setTable(e.target.value)}
            disabled={tables.loading || tableList.length === 0}
          >
            {tableList.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} ({formatCount(t.rows)} baris)
              </option>
            ))}
          </select>
        </div>

        <SearchInput
          id={`${ids}-q`}
          value={q}
          onChange={setQ}
          label="Cari di tabel"
          placeholder="Cari isi kolom…"
        />

        <div className="field">
          <label htmlFor={`${ids}-col`}>Kolom</label>
          <select id={`${ids}-col`} value={column} onChange={(e) => setColumn(e.target.value)}>
            <option value="">Semua kolom teks</option>
            {filterable.map((col) => (
              <option key={col.name} value={col.name}>
                {col.name}
              </option>
            ))}
          </select>
        </div>

        <div className="db-export">
          <button type="button" className="btn" onClick={() => exportAs("xlsx")} disabled={!table || total === 0}>
            <Glyph name="documentDownload" size={20} /> Excel
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => exportAs("csv")}
            disabled={!table || total === 0}
          >
            CSV
          </button>
        </div>
      </section>

      <section className="region" aria-label="Isi tabel">
        {exportError ? (
          <Notice tone="danger" title="Gagal mengunduh">
            {exportError}
          </Notice>
        ) : null}

        {tables.error ? (
          <Notice tone="danger" title="Gagal memuat daftar tabel">
            {API.errorMessage(tables.error)}
          </Notice>
        ) : null}

        {data.error ? (
          <Notice tone="danger" title="Gagal memuat data">
            {API.errorMessage(data.error)}
          </Notice>
        ) : null}

        {data.loading || tables.loading ? (
          <SkeletonRows count={6} label="Memuat data" />
        ) : !table ? (
          <EmptyState icon="layers" title="Belum ada tabel" body="Tidak ada tabel yang dapat ditampilkan." />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={filtered ? "searchNormal" : "layers"}
            title={filtered ? "Tidak ada hasil" : "Tabel kosong"}
            body={
              filtered
                ? "Tidak ada baris yang cocok dengan pencarian ini."
                : `Tabel ${table} belum memiliki data.`
            }
          />
        ) : (
          <>
            <p className="cell-meta db-count">
              {filtered
                ? `${formatCount(total)} baris cocok`
                : `${formatCount(total)} baris`}
              {pageCount > 1 ? ` · halaman ${page} dari ${pageCount}` : ""}
            </p>

            <div className="records-table db-table">
              <table className="table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.name} scope="col">
                        {col.name}
                        <span className="db-type">{col.dataType}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {columns.map((col) => {
                        const cell = renderCell(row[col.name]);
                        const name = labelFor(col, row[col.name], labels);
                        return (
                          /* The class goes on the inner span, never on the td:
                             .cell-text is display:block, which would override
                             the td's display:table-cell and drop the cell out
                             of the table's column grid. */
                          <td key={col.name}>
                            {name ? (
                              (() => {
                                const lines = refLines(name);
                                const full = [lines.primary, lines.secondary, cell.text]
                                  .filter(Boolean)
                                  .join(" · ");
                                return (
                                  <span className="db-ref" title={full}>
                                    <span className="db-ref-name">{truncate(lines.primary, 40)}</span>
                                    {lines.secondary ? (
                                      <span className="db-ref-owner">{truncate(lines.secondary, 30)}</span>
                                    ) : null}
                                    <span className="db-ref-id">{truncate(cell.text, 12)}</span>
                                  </span>
                                );
                              })()
                            ) : (
                              <span
                                className={cell.muted ? "cell-muted" : "cell-text"}
                                title={cell.text}
                              >
                                {truncate(cell.text)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Narrow screens: a table of unknown width cannot scroll usefully,
                so each row becomes a definition list of column/value pairs. */}
            <ul className="rows records-list db-list">
              {rows.map((row, i) => (
                <li key={i} className="db-card">
                  {columns.map((col) => {
                    const cell = renderCell(row[col.name]);
                    const name = labelFor(col, row[col.name], labels);
                    return (
                      <div key={col.name} className="db-pair">
                        <span className="db-key">{col.name}</span>
                        {name ? (
                          <span className="cell-text">
                            {truncate(refLines(name).primary, 80)}
                            {refLines(name).secondary ? (
                              <span className="db-ref-owner">
                                {truncate(refLines(name).secondary!, 40)}
                              </span>
                            ) : null}
                            <span className="db-ref-id">{truncate(cell.text, 16)}</span>
                          </span>
                        ) : (
                          <span className={cell.muted ? "cell-muted" : "cell-text"}>
                            {truncate(cell.text, 120)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </li>
              ))}
            </ul>

            {pageCount > 1 ? (
              <nav className="db-pager" aria-label="Halaman data">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page <= 1}
                >
                  Sebelumnya
                </button>
                <span className="cell-meta">
                  Halaman {page} dari {pageCount}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
                  disabled={page >= pageCount}
                >
                  Berikutnya
                </button>
              </nav>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}
